import { Link } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Fade, Grow, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    Lock as LockIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { api } from "../../services/api";
import { FormState, getErrorIcon, getErrorSeverity } from "./PasswordFunctions";

type PasswordResetProps = {
    formState: FormState;
    setFormState: React.Dispatch<React.SetStateAction<FormState>>
}

const RenderPasswordResetStep = ({ formState, setFormState }: PasswordResetProps) => {
    const handlePasswordChange = (field: 'newPassword' | 'confirmPassword') =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setFormState(prev => ({
                ...prev,
                [field]: event.target.value,
                error: null,
                errorType: null,
            }));
        };

    const togglePasswordVisibility = (field: 'showNewPassword' | 'showConfirmPassword') => {
        setFormState(prev => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handleResetPassword = async () => {
        // Validar senha
        if (String(formState.newPassword).length < 4) {
            setFormState(prev => ({
                ...prev,
                error: 'A senha deve ter pelo menos 4 caracteres',
                errorType: 'weak-password',
            }));
            return;
        }

        // Verificar se as senhas coincidem
        if (formState.newPassword !== formState.confirmPassword) {
            setFormState(prev => ({
                ...prev,
                error: 'As senhas não coincidem. Verifique e tente novamente.',
                errorType: 'password-mismatch',
            }));
            return;
        }

        setFormState(prev => ({
            ...prev,
            isLoading: true,
            error: null,
            errorType: null,
        }));

        try {
            const data = {
                id_usuario: formState.id_usuario,
                senha_usuario: formState.newPassword,
            };
            const response = await api.post(`/redefineusuario`, data);
            console.log(response);
            if (response.data.success) {
                setFormState(prev => ({
                    ...prev,
                    isLoading: false,
                    isPasswordReset: true,
                    isSuccess: true,
                }));
            } else {
                setFormState(prev => ({
                    ...prev,
                    isLoading: false,
                    error: 'Erro interno do servidor. Tente novamente mais tarde.',
                    errorType: 'server',
                }));
            }

        } catch {
            setFormState(prev => ({
                ...prev,
                isLoading: false,
                error: 'Erro interno do servidor. Tente novamente mais tarde.',
                errorType: 'server',
            }));
        }
    };

    return (
        <Grow in timeout={800}>
            <Box>
                <Alert
                    severity="success"
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        borderColor: 'success.main',
                    }}
                >
                    <Typography variant="body2">
                        Email verificado! Agora defina sua nova senha.
                    </Typography>
                </Alert>

                <Typography variant="h6" gutterBottom textAlign="center" sx={{ mb: 3 }}>
                    Redefinir Senha
                </Typography>

                <TextField
                    fullWidth
                    type={formState.showNewPassword ? 'text' : 'password'}
                    label="Nova Senha"
                    variant="outlined"
                    value={formState.newPassword}
                    onChange={handlePasswordChange('newPassword')}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => togglePasswordVisibility('showNewPassword')}
                                    edge="end"
                                >
                                    {formState.showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            },
                            '&.Mui-focused': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                            },
                        },
                    }}
                />

                <TextField
                    fullWidth
                    type={formState.showConfirmPassword ? 'text' : 'password'}
                    label="Confirmar Nova Senha"
                    variant="outlined"
                    value={formState.confirmPassword}
                    onChange={handlePasswordChange('confirmPassword')}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => togglePasswordVisibility('showConfirmPassword')}
                                    edge="end"
                                >
                                    {formState.showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            },
                            '&.Mui-focused': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                            },
                        },
                    }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                    A senha deve ter pelo menos 8 caracteres, incluindo uma letra maiúscula, uma minúscula e um número.
                </Typography>

                {formState.error && (
                    <Fade in>
                        <Alert
                            severity={getErrorSeverity(formState)}
                            icon={getErrorIcon(formState)}
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                '& .MuiAlert-icon': {
                                    fontSize: 20,
                                },
                            }}
                        >
                            <Typography variant="body2" component="div">
                                {formState.error}
                            </Typography>
                        </Alert>
                    </Fade>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleResetPassword}
                    disabled={!formState.newPassword || !formState.confirmPassword || formState.isLoading}
                    startIcon={
                        formState.isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <CheckCircleIcon />
                        )
                    }
                    sx={{
                        py: 1.5,
                        mb: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                        boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                            background: 'linear-gradient(45deg, #45a049 30%, #3d8b40 90%)',
                        },
                        '&:disabled': {
                            transform: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        },
                    }}
                >
                    {formState.isLoading ? 'Redefinindo...' : 'Confirmar Nova Senha'}
                </Button>

                <Link to="/login">
                    <Button
                        fullWidth
                        variant="text"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            py: 1,
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(33, 150, 243, 0.04)',
                                transform: 'translateX(-4px)',
                            },
                        }}
                    >
                        Voltar ao Login
                    </Button>
                </Link>
            </Box>
        </Grow>
    );
}

export default RenderPasswordResetStep;